// Percorso file: src/utils/enrolDati.js
//
// I DATI DELL'ISCRIZIONE: il calendario di Perth, l'età compiuta, la forma
// dell'email, e la lettura di che cosa ha risposto il server.
//
// ⚠️ MODULO PURO, E DEVE RESTARLO: nessun React, nessun hook, nessun import
// di AuthContext, nessun import di alcun genere. È la ragione stessa per cui
// questo file esiste: chi ha bisogno di una di queste funzioni non deve
// portarsi dietro nient'altro.
//
// DA DOVE VIENE (cantiere «igiene di fine enrollment»). Questi sei export
// stavano in `src/pages/enrolShared.jsx`, accanto ai due componenti JSX che
// lì restano. Erano già puri, ma vivevano in mezzo al JSX: chiunque ne
// volesse una — `utils/categoria.js` compreso, cioè un modulo di `utils/`
// che importava da `pages/` — si tirava dietro React, il dizionario delle
// lingue e il foglio di stile dell'aiuto. Lo spostamento è SOLO uno
// spostamento: stesse righe, stessi corpi, stessi commenti, nessuna riga di
// comportamento cambiata.
//
// ⚠️ IL PATTO COL DIZIONARIO SOPRAVVIVE ALLO SPOSTAMENTO. Le stringhe
// `err.unreachable`, `err.emailTaken`, `err.badDetails` e `err.serverFail`
// che `messaggioDiErrore` restituisce NON sono testo a schermo: sono CHIAVI
// di `src/pages/enrolLingue.js`, dove stanno le parole. Non è un import — il
// modulo resta puro — ma è un legame, e come tale non si tocca: cambiarle
// qui senza cambiarle là lascerebbe l'avviso muto.

// Il genere si raccoglie M/F perché M/F è ciò che il server accetta (D5).
// L'etichetta resta la parola per esteso, il valore è la lettera.
export const GENERI = [
  { valore: "M", etichetta: "Male" },
  { valore: "F", etichetta: "Female" },
];

// Oggi a Perth, in forma YYYY-MM-DD.
//
// Il calendario del club è quello di Perth e il server calcola la minore età
// sull'ora di Perth (AWST, senza ora legale). Chi compila da un altro fuso
// vedrebbe altrimenti una risposta diversa da quella del server proprio nel
// giorno del diciottesimo compleanno. Se `Intl` non sa la zona si ripiega sulla
// data locale: è una cortesia, e la parola definitiva resta del server.
export function oggiAPerth() {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: "Australia/Perth",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date());
  } catch {
    const ora = new Date();
    const mm = String(ora.getMonth() + 1).padStart(2, "0");
    const gg = String(ora.getDate()).padStart(2, "0");
    return `${ora.getFullYear()}-${mm}-${gg}`;
  }
}

// Età compiuta alla data indicata, o null se la data non è leggibile.
// Stessa regola di calendario del server (isMinorDaDob): compleanno non ancora
// arrivato quest'anno = un anno in meno.
export function etaCompiuta(dob, oggi) {
  const nato = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dob || "").trim());
  const adesso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(oggi || "").trim());
  if (!nato || !adesso) return null;

  const [anno, mese, giorno] = nato.slice(1).map(Number);
  const [annoOggi, meseOggi, giornoOggi] = adesso.slice(1).map(Number);

  // Data inesistente ("2010-02-31" supera la forma ma non il calendario).
  const prova = new Date(Date.UTC(anno, mese - 1, giorno));
  if (
    prova.getUTCFullYear() !== anno ||
    prova.getUTCMonth() !== mese - 1 ||
    prova.getUTCDate() !== giorno
  ) {
    return null;
  }

  let eta = annoOggi - anno;
  if (meseOggi < mese || (meseOggi === mese && giornoOggi < giorno)) eta -= 1;
  return eta;
}

// Forma dell'email: il controllo minimo che evita il refuso evidente. La
// casella esiste davvero solo quando ci arriva la posta.
export function emailBenFormata(valore) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(valore || "").trim());
}

// Il messaggio del server, quando c'è: le rotte v2 rispondono
// { error: "..." } e quei testi sono già scritti per essere letti da chi
// compila. Il corpo arriva come testo grezzo da api.js.
export function messaggioDelServer(err) {
  if (!err || typeof err.body !== "string") return "";
  try {
    const corpo = JSON.parse(err.body);
    return corpo && typeof corpo.error === "string" ? corpo.error : "";
  } catch {
    return "";
  }
}

// Che cosa dire di un'iscrizione non riuscita.
//
// ⚠️ SI DISTINGUE IL RIFIUTO DAL GUASTO. Se il server ha risposto, `err.status`
// c'è (lo attacca api.js) e il motivo è nella risposta; se non c'è risposta
// affatto — rete assente, backend irraggiungibile — non si può dire alla
// persona che i suoi dati sono sbagliati. È il difetto della vecchia login,
// che mostrava «controlla le credenziali» anche quando il server non era stato
// nemmeno raggiunto: non si eredita.
//
// ⚠️ CANTIERE 2 — NON TORNA PIÙ UNA STRINGA, TORNA UN AVVISO. Le parole sono
// le stesse, alla lettera; cambia il fatto che ora viaggiano insieme alla loro
// CHIAVE, che è l'identità dell'avviso. Serviva perché le pagine mostrano sotto
// l'inglese una riga d'aiuto tradotta, e una traduzione si trova per chiave e
// non per frase — ma la ragione vera è un'altra, ed è di funzione: finché
// l'identità di un messaggio era il suo testo, cambiare una virgola ne
// cambiava il significato per il codice. Vedi `Enrol.jsx`, varco al modulo
// famiglia.
//
// Due forme sole:
//   { chiave }  un messaggio NOSTRO: le parole stanno in enrolLingue.js
//   { testo }   le parole del SERVER, così come arrivano. Vivono in
//               backend/server.js, fuori da questo perimetro, e non hanno
//               traduzione: si mostrano in inglese.
export function messaggioDiErrore(err) {
  const stato = err && err.status;

  if (!stato) {
    return { chiave: "err.unreachable" };
  }

  const dalServer = messaggioDelServer(err);

  if (stato === 409) {
    return dalServer ? { testo: dalServer } : { chiave: "err.emailTaken" };
  }

  if (stato === 400) {
    // Il testo del server è preciso (minore età, ruolo non ammesso, data non
    // valida, termini non accettati): si mostra quello.
    return dalServer ? { testo: dalServer } : { chiave: "err.badDetails" };
  }

  return { chiave: "err.serverFail" };
}
